import {
  apyToApr,
  calculateRateForCompoundType,
  calculateRateForCompoundType2,
  TimeScale,
} from '../../../../utils/parsing'
import { CAction } from './types'

/**
 * Shared struct field layout for Compound V2 style lenses.
 *
 * Struct order:
 *   cToken(0), exchangeRateCurrent(1), supplyRatePerBlock(2),
 *   borrowRatePerBlock(3), reserveFactorMantissa(4), totalBorrows(5),
 *   totalReserves(6), totalSupply(7), totalCash(8), isListed(9),
 *   collateralFactorMantissa(10), underlyingAssetAddress(11),
 *   cTokenDecimals(12), underlyingDecimals(13), compSupplySpeed(14),
 *   compBorrowSpeed(15), borrowCap(16)
 *
 * Fields 14-16 only present on Lodestar (17-field variant).
 */

const MAX_RATE = 9999.99

function capRate(rate: number): number {
  if (!Number.isFinite(rate)) return MAX_RATE
  return Math.min(rate, MAX_RATE)
}

function pick(meta: any, key: string, index: number) {
  if (meta == null) return undefined
  if (key in meta) return meta[key]
  if (Array.isArray(meta)) return meta[index]
  return meta[key]
}

const DEFAULT_PAUSED_ACTIONS = {
  [CAction.MINT]: false,
  [CAction.REDEEM]: false,
  [CAction.BORROW]: false,
  [CAction.REPAY]: false,
  [CAction.SEIZE]: false,
  [CAction.LIQUIDATE]: false,
  [CAction.TRANSFER]: false,
  [CAction.ENTER_MARKET]: false,
  [CAction.EXIT_MARKET]: false,
}

function parseBaseFields(meta: any) {
  return {
    cToken: pick(meta, 'cToken', 0) ?? '',
    exchangeRateCurrent: pick(meta, 'exchangeRateCurrent', 1) ?? 0n,
    supplyRatePerBlock: pick(meta, 'supplyRatePerBlock', 2) ?? 0n,
    borrowRatePerBlock: pick(meta, 'borrowRatePerBlock', 3) ?? 0n,
    reserveFactorMantissa: pick(meta, 'reserveFactorMantissa', 4) ?? 0n,
    totalBorrows: pick(meta, 'totalBorrows', 5) ?? 0n,
    totalReserves: pick(meta, 'totalReserves', 6) ?? 0n,
    totalSupply: pick(meta, 'totalSupply', 7) ?? 0n,
    // deliberately NOT `?? 0n`: an absent cash field must stay `undefined`
    // so the converter falls back to the derived figure instead of
    // reporting a live market as empty. See `convert/liquidity.ts`.
    totalCash: pick(meta, 'totalCash', 8),
    isListed: pick(meta, 'isListed', 9) ?? true,
    collateralFactorMantissa: pick(meta, 'collateralFactorMantissa', 10) ?? 0n,
    underlyingAssetAddress: pick(meta, 'underlyingAssetAddress', 11) ?? '',
    cTokenDecimals: pick(meta, 'cTokenDecimals', 12) ?? 0n,
    underlyingDecimals: pick(meta, 'underlyingDecimals', 13) ?? 0n,
    compSupplySpeed: pick(meta, 'compSupplySpeed', 14) ?? 0n,
    compBorrowSpeed: pick(meta, 'compBorrowSpeed', 15) ?? 0n,
    borrowCap: pick(meta, 'borrowCap', 16) ?? 0n,
    isBorrowAllowed: true,
    pausedActions: DEFAULT_PAUSED_ACTIONS,
  }
}

/**
 * Reader for Lodestar / Tender: uses calculateRateForCompoundType2
 * (hardcoded 12s block time factor).
 */
export function readLodestarCTokenMetadata(
  meta: any,
  token: any,
  unitSeconds?: number,
) {
  const base = parseBaseFields(meta)
  return {
    ...base,
    depositRate: capRate(
      apyToApr(
        calculateRateForCompoundType2(
          base.supplyRatePerBlock?.toString() ?? '0',
          unitSeconds,
        ) / 100,
      ) * 100,
    ),
    variableBorrowRate: capRate(
      apyToApr(
        calculateRateForCompoundType2(
          base.borrowRatePerBlock?.toString() ?? '0',
          unitSeconds,
        ) / 100,
      ) * 100,
    ),
  }
}

/**
 * Reader for standard Compound V2 forks (Compound V2 OG, Flux Finance,
 * WePiggy, Cream Finance on ETH/BNB, Gamma): uses calculateRateForCompoundType
 * with actual chain block time.
 */
export function readCompoundV2CTokenMetadata(
  meta: any,
  token: any,
  unitSeconds?: number,
) {
  const base = parseBaseFields(meta)
  return {
    ...base,
    depositRate: capRate(
      apyToApr(
        calculateRateForCompoundType(
          base.supplyRatePerBlock?.toString() ?? '0',
          token.chainId,
          TimeScale.BLOCK,
          unitSeconds,
        ) / 100,
      ) * 100,
    ),
    variableBorrowRate: capRate(
      apyToApr(
        calculateRateForCompoundType(
          base.borrowRatePerBlock?.toString() ?? '0',
          token.chainId,
          TimeScale.BLOCK,
          unitSeconds,
        ) / 100,
      ) * 100,
    ),
  }
}

/**
 * Cream Polygon/Arbitrum variant: 17 fields with shifted indices.
 *
 * Struct order:
 *   cToken(0), exchangeRateCurrent(1), supplyRatePerBlock(2),
 *   borrowRatePerBlock(3), reserveFactorMantissa(4), totalBorrows(5),
 *   totalReserves(6), totalSupply(7), totalCash(8),
 *   totalCollateralTokens(9), isListed(10), collateralFactorMantissa(11),
 *   underlyingAssetAddress(12), cTokenDecimals(13), underlyingDecimals(14),
 *   version(15), collateralCap(16)
 */
function parseCreamV2BaseFields(meta: any) {
  return {
    cToken: pick(meta, 'cToken', 0) ?? '',
    exchangeRateCurrent: pick(meta, 'exchangeRateCurrent', 1) ?? 0n,
    supplyRatePerBlock: pick(meta, 'supplyRatePerBlock', 2) ?? 0n,
    borrowRatePerBlock: pick(meta, 'borrowRatePerBlock', 3) ?? 0n,
    reserveFactorMantissa: pick(meta, 'reserveFactorMantissa', 4) ?? 0n,
    totalBorrows: pick(meta, 'totalBorrows', 5) ?? 0n,
    totalReserves: pick(meta, 'totalReserves', 6) ?? 0n,
    totalSupply: pick(meta, 'totalSupply', 7) ?? 0n,
    // deliberately NOT `?? 0n`: an absent cash field must stay `undefined`
    // so the converter falls back to the derived figure instead of
    // reporting a live market as empty. See `convert/liquidity.ts`.
    totalCash: pick(meta, 'totalCash', 8),
    isListed: pick(meta, 'isListed', 10) ?? true,
    collateralFactorMantissa: pick(meta, 'collateralFactorMantissa', 11) ?? 0n,
    underlyingAssetAddress: pick(meta, 'underlyingAssetAddress', 12) ?? '',
    cTokenDecimals: pick(meta, 'cTokenDecimals', 13) ?? 0n,
    underlyingDecimals: pick(meta, 'underlyingDecimals', 14) ?? 0n,
    isBorrowAllowed: true,
    pausedActions: DEFAULT_PAUSED_ACTIONS,
  }
}

export function readCreamV2CTokenMetadata(
  meta: any,
  token: any,
  unitSeconds?: number,
) {
  const base = parseCreamV2BaseFields(meta)
  return {
    ...base,
    depositRate: capRate(
      apyToApr(
        calculateRateForCompoundType(
          base.supplyRatePerBlock?.toString() ?? '0',
          token.chainId,
          TimeScale.BLOCK,
          unitSeconds,
        ) / 100,
      ) * 100,
    ),
    variableBorrowRate: capRate(
      apyToApr(
        calculateRateForCompoundType(
          base.borrowRatePerBlock?.toString() ?? '0',
          token.chainId,
          TimeScale.BLOCK,
          unitSeconds,
        ) / 100,
      ) * 100,
    ),
  }
}
